import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { api } from '../services/api';
import DatePicker from './DatePicker';
import LoadingSpinner from './common/LoadingSpinner';
import './PostpartumView.css';
import './ModernSetup.css';

function PostpartumView({ userId, profile, onProfileUpdate }) {
  const { t } = useTranslation();
  const [postpartumInfo, setPostpartumInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [daysPostpartum, setDaysPostpartum] = useState(0);
  const [showDeliveryDateForm, setShowDeliveryDateForm] = useState(false);
  const [showPostpartumInfoForm, setShowPostpartumInfoForm] = useState(false);
  const [error, setError] = useState('');
  const [deliveryDate, setDeliveryDate] = useState('');
  const [postpartumFormData, setPostpartumFormData] = useState({
    deliveryType: '',
    isBreastfeeding: null,
    babyWeight: '',
    deliveryNotes: ''
  });

  const loadPostpartumData = useCallback(async (days) => {
    try {
      setLoading(true);
      setError('');
      
      console.log('Loading postpartum data for', days, 'days postpartum');
      
      const infoData = await api.getPostpartumInfo();
      
      console.log('Loaded postpartum data:', { 
        info: infoData, 
        appointments: 'loaded',
        warnings: 'loaded'
      });
      
      setPostpartumInfo(infoData);
    } catch (error) {
      console.error('Error loading postpartum data:', error);
      setError(t('postpartum.error.load', 'Unable to load postpartum data. Please try again.'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    // If no profile, show delivery date form
    if (!profile) {
      setShowDeliveryDateForm(true);
      setLoading(false);
      return;
    }

    // Calculate days postpartum from delivery date
    if (profile.deliveryDate) {
      const delivery = new Date(profile.deliveryDate);
      const today = new Date();
      const diffTime = Math.abs(today - delivery);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      setDaysPostpartum(diffDays);
      setDeliveryDate(profile.deliveryDate);
      
      // Check if postpartum info is complete
      if (!profile.deliveryType || profile.isBreastfeeding === null || profile.isBreastfeeding === undefined) {
        setShowPostpartumInfoForm(true);
        setLoading(false);
        setPostpartumFormData({
          deliveryType: profile.deliveryType || '',
          isBreastfeeding: profile.isBreastfeeding !== null && profile.isBreastfeeding !== undefined ? profile.isBreastfeeding : null,
          babyWeight: profile.babyWeight || '',
          deliveryNotes: profile.deliveryNotes || ''
        });
        return; // Don't load data yet if form needs to be shown
      }
      
      // Info is complete, load data
      loadPostpartumData(diffDays);
    } else {
      // No delivery date set - show form to enter it
      setShowDeliveryDateForm(true);
      setLoading(false);
    }
  }, [profile, loadPostpartumData]);

  const handleDeliveryDateSubmit = async (e) => {
    e.preventDefault();
    
    if (!deliveryDate || !deliveryDate.trim()) {
      setError(t('postpartum.error.deliveryDateRequired', 'Please enter your delivery date.'));
      return;
    }
    
    // DatePicker already provides ISO format
    const isoDate = deliveryDate;
    if (!isoDate || !isoDate.match(/^\d{4}-\d{2}-\d{2}$/)) {
      setError(t('postpartum.error.deliveryDateInvalid', 'Invalid date. Please select a valid date.'));
      return;
    }
    
    try {
      setError('');
      // Update profile with delivery date
      const updatedProfile = {
        ...profile,
        deliveryDate: isoDate
      };
      
      const savedProfile = await api.savePregnancyProfile(updatedProfile);
      
      // Calculate days postpartum
      const delivery = new Date(isoDate);
      const today = new Date();
      const diffTime = Math.abs(today - delivery);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      setDaysPostpartum(diffDays);
      setShowDeliveryDateForm(false);
      
      // Notify parent to reload profile
      if (onProfileUpdate) {
        await onProfileUpdate();
      }
      
      // Show postpartum info form if needed
      if (!savedProfile.deliveryType || savedProfile.isBreastfeeding === null || savedProfile.isBreastfeeding === undefined) {
        setShowPostpartumInfoForm(true);
        setPostpartumFormData({
          deliveryType: savedProfile.deliveryType || '',
          isBreastfeeding: savedProfile.isBreastfeeding !== null && savedProfile.isBreastfeeding !== undefined ? savedProfile.isBreastfeeding : null,
          babyWeight: savedProfile.babyWeight || '',
          deliveryNotes: savedProfile.deliveryNotes || ''
        });
      } else {
        // Load postpartum data if info is complete
        await loadPostpartumData(diffDays);
      }
    } catch (error) {
      console.error('Error saving delivery date:', error);
      setError(t('postpartum.error.deliveryDateSave', 'Failed to save delivery date. Please try again.'));
    }
  };

  const handlePostpartumInfoSubmit = async (e) => {
    e.preventDefault();
    
    if (!postpartumFormData.deliveryType) {
      setError(t('postpartum.error.deliveryTypeRequired', 'Please select your delivery type.'));
      return;
    }
    
    if (postpartumFormData.isBreastfeeding === null) {
      setError(t('postpartum.error.breastfeedingRequired', 'Please indicate if you are breastfeeding.'));
      return;
    }
    
    try {
      setError('');
      const updatedProfile = {
        ...profile,
        deliveryType: postpartumFormData.deliveryType,
        isBreastfeeding: postpartumFormData.isBreastfeeding,
        babyWeight: postpartumFormData.babyWeight ? parseFloat(postpartumFormData.babyWeight) : null,
        deliveryNotes: postpartumFormData.deliveryNotes
      };
      
      const savedProfile = await api.savePregnancyProfile(updatedProfile);
      
      setShowPostpartumInfoForm(false);
      
      // Notify parent to reload profile first
      if (onProfileUpdate) {
        await onProfileUpdate();
      }
      
      // Calculate days postpartum and load data
      if (savedProfile.deliveryDate) {
        const delivery = new Date(savedProfile.deliveryDate);
        const today = new Date();
        const diffTime = Math.abs(today - delivery);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        setDaysPostpartum(diffDays);
        
        // Load postpartum data
        await loadPostpartumData(diffDays);
      }
    } catch (error) {
      console.error('Error saving postpartum info:', error);
      setError(t('postpartum.error.infoSave', 'Failed to save information. Please try again.'));
    }
  };

  if (loading) {
    return (
      <div className="postpartum-view">
        <LoadingSpinner message={t('postpartum.loading', 'Loading your postpartum information...')} />
      </div>
    );
  }

  if (showDeliveryDateForm) {
    return (
      <div className="postpartum-view">
        <div className="modern-setup-card">
          <div className="setup-header">
            <h2>👶 {t('postpartum.setup.title', 'Set Up Postpartum Tracking')}</h2>
            <p className="setup-subtitle">
              {t('postpartum.setup.description', 'Enter your delivery date to get started')}
            </p>
          </div>
          <form onSubmit={handleDeliveryDateSubmit} className="modern-setup-form">
            <div className="modern-form-group">
              <label htmlFor="deliveryDate">
                📅 {t('postpartum.delivery.date', 'Delivery Date')}
                <span className="required-asterisk"> *</span>
              </label>
              <DatePicker
                id="deliveryDate"
                name="deliveryDate"
                value={deliveryDate}
                onChange={(e) => setDeliveryDate(e.target.value)}
                max={new Date().toISOString().split('T')[0]}
                placeholder="MM/DD/YYYY"
                required
              />
            </div>
            {error && <div className="postpartum-form-error">{error}</div>}
            <button type="submit" className="btn-modern-primary">
              💾 {t('postpartum.save.start', 'Start Tracking')}
            </button>
          </form>
        </div>
      </div>
    );
  }

  if (showPostpartumInfoForm) {
    return (
      <div className="postpartum-view">
        <div className="modern-setup-card">
          <div className="setup-header">
            <h2>📋 {t('postpartum.info.title', 'Postpartum Information')}</h2>
            <p className="setup-subtitle">
              {t('postpartum.info.description', 'Help us personalize your care recommendations')}
            </p>
            {profile?.deliveryDate && (
              <div className="info-badge">
                📅 {new Date(profile.deliveryDate).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </div>
            )}
          </div>
          <form onSubmit={handlePostpartumInfoSubmit} className="modern-setup-form">
            <div className="modern-form-row">
              <div className="modern-form-group required">
                <label htmlFor="deliveryType">
                  {t('postpartum.delivery.type', 'Delivery Type')} *
                </label>
                <select
                  id="deliveryType"
                  value={postpartumFormData.deliveryType}
                  onChange={(e) => setPostpartumFormData({...postpartumFormData, deliveryType: e.target.value})}
                  required
                  className="modern-input"
                >
                  <option value="">{t('postpartum.select', 'Select...')}</option>
                  <option value="VAGINAL">{t('postpartum.delivery.type.vaginal', 'Vaginal Delivery')}</option>
                  <option value="C_SECTION">{t('postpartum.delivery.type.csection', 'C-Section')}</option>
                </select>
              </div>

              <div className="modern-form-group required">
                <label>
                  {t('postpartum.breastfeeding.status', 'Breastfeeding?')} *
                </label>
                <div className="radio-group-modern">
                  <label className="radio-option-modern">
                    <input
                      type="radio"
                      name="isBreastfeeding"
                      value="true"
                      checked={postpartumFormData.isBreastfeeding === true}
                      onChange={() => setPostpartumFormData({...postpartumFormData, isBreastfeeding: true})}
                      required
                    />
                    <span>{t('postpartum.answer.yes', 'Yes')}</span>
                  </label>
                  <label className="radio-option-modern">
                    <input
                      type="radio"
                      name="isBreastfeeding"
                      value="false"
                      checked={postpartumFormData.isBreastfeeding === false}
                      onChange={() => setPostpartumFormData({...postpartumFormData, isBreastfeeding: false})}
                      required
                    />
                    <span>{t('postpartum.answer.no', 'No')}</span>
                  </label>
                </div>
              </div>
            </div>

            <div className="modern-form-row">
              <div className="modern-form-group">
                <label htmlFor="babyWeight">
                  {t('postpartum.baby.weight', "Baby's Weight (lbs)")}
                </label>
                <input
                  type="number"
                  id="babyWeight"
                  step="0.1"
                  min="0"
                  value={postpartumFormData.babyWeight}
                  onChange={(e) => setPostpartumFormData({...postpartumFormData, babyWeight: e.target.value})}
                  placeholder={t('postpartum.baby.weight.placeholder', '7.5')}
                  className="modern-input"
                />
              </div>

              <div className="modern-form-group">
                <label htmlFor="deliveryNotes">
                  {t('postpartum.delivery.notes', 'Notes')}
                </label>
                <input
                  type="text"
                  id="deliveryNotes"
                  value={postpartumFormData.deliveryNotes}
                  onChange={(e) => setPostpartumFormData({...postpartumFormData, deliveryNotes: e.target.value})}
                  placeholder={t('postpartum.delivery.notes.placeholder', 'Additional info...')}
                  className="modern-input"
                />
              </div>
            </div>

            {error && <div className="postpartum-form-error">{error}</div>}
            <button type="submit" className="btn-modern-primary">
              💾 {t('postpartum.save.info', 'Save & Continue')}
            </button>
          </form>
        </div>
      </div>
    );
  }


  // If no profile at all, show setup message
  if (!postpartumInfo && !showDeliveryDateForm && !showPostpartumInfoForm && !loading && !profile) {
    return (
      <div className="postpartum-view">
        <section className="data-section setup-section">
          <h2>👶 {t('postpartum.setup.title', 'Set Up Postpartum Tracking')}</h2>
          <p className="setup-description">
            {t('postpartum.setup.description', 'Enter your delivery date to start tracking your postpartum recovery.')}
          </p>
          <button 
            onClick={() => setShowDeliveryDateForm(true)}
            className="btn-submit-inline"
          >
            📅 {t('postpartum.enter.date', 'Enter Delivery Date')}
          </button>
        </section>
      </div>
    );
  }

  const weeksPostpartum = Math.floor(daysPostpartum / 7);
  const daysRemainder = daysPostpartum % 7;

  return (
    <div className="postpartum-view">
      {/* Header Stats */}
      {profile && (
        <div className="view-header">
          <div className="header-stats">
            <div className="stat-box">
              <div className="stat-number">{daysPostpartum}</div>
              <div className="stat-label">{t('postpartum.days', 'Days Postpartum')}</div>
            </div>
            <div className="stat-box">
              <div className="stat-number-small">
                {weeksPostpartum}w {daysRemainder}d
              </div>
              <div className="stat-label">{t('postpartum.weeks', 'Weeks Postpartum')}</div>
            </div>
            {profile?.deliveryDate && (
              <div className="stat-box">
                <div className="stat-number-small">
                  {new Date(profile.deliveryDate).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric'
                  })}
                </div>
                <div className="stat-label">{t('postpartum.delivery.date', 'Delivery Date')}</div>
              </div>
            )}
            {profile?.deliveryType && (
              <div className="stat-box">
                <div className="stat-number-small">
                  {profile.deliveryType === 'VAGINAL'
                    ? `👶 ${t('postpartum.delivery.type.vaginal', 'Vaginal')}`
                    : `🏥 ${t('postpartum.delivery.type.csection', 'C-Section')}`}
                </div>
                <div className="stat-label">{t('postpartum.delivery.type', 'Delivery Type')}</div>
              </div>
            )}
            {profile?.isBreastfeeding !== null && (
              <div className="stat-box">
                <div className="stat-number-small">
                  {profile.isBreastfeeding
                    ? `🤱 ${t('postpartum.answer.yes', 'Yes')}`
                    : `🍼 ${t('postpartum.answer.no', 'No')}`}
                </div>
                <div className="stat-label">{t('postpartum.breastfeeding.status', 'Breastfeeding')}</div>
              </div>
            )}
          </div>
          <button 
            className="btn-edit" 
            onClick={() => {
              if (!profile.deliveryDate) {
                setShowDeliveryDateForm(true);
              } else {
                setShowPostpartumInfoForm(true);
              }
            }}
          >
            ✏️ {t('action.edit', 'Edit Information')}
          </button>
        </div>
      )}

      {/* Postpartum Summary - Show key info */}
      {postpartumInfo && (
        <div className="postpartum-summary">
          <h3>📋 {t('postpartum.summary', 'Postpartum Summary')}</h3>
          {postpartumInfo.postpartumPeriods && (
            <div className="summary-section">
              <h4>📅 {t('postpartum.periods', 'Postpartum Periods')}</h4>
              <ul>
                {postpartumInfo.postpartumPeriods.map((period, idx) => (
                  <li key={idx}>
                    <strong>{period.period}:</strong> {period.description}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {postpartumInfo.physicalRecovery && (
            <div className="summary-section">
              <h4>💪 {t('postpartum.recovery', 'Physical Recovery')}</h4>
              <p>{postpartumInfo.physicalRecovery.immediate?.description || 'Recovery information available'}</p>
            </div>
          )}
        </div>
      )}

      {/* Action Buttons - Show if profile has delivery date (data loads on route pages) */}
      {profile && profile.deliveryDate && (
        <div className="postpartum-actions">
          <div className="action-buttons-grid">
            <Link 
              to={`/postpartum/${userId}/overview`}
              className="action-button"
            >
              📊 {t('postpartum.nav.overview', 'Overview')}
            </Link>
            <Link 
              to={`/postpartum/${userId}/recovery`}
              className="action-button"
            >
              💪 {t('postpartum.nav.recovery', 'Recovery')}
            </Link>
            <Link 
              to={`/postpartum/${userId}/nutrition`}
              className="action-button"
            >
              🥗 {t('postpartum.nav.nutrition', 'Nutrition')}
            </Link>
            <Link 
              to={`/postpartum/${userId}/appointments`}
              className="action-button"
            >
              📅 {t('postpartum.nav.appointments', 'Appointments')}
            </Link>
            <Link 
              to={`/postpartum/${userId}/warnings`}
              className="action-button"
            >
              ⚠️ {t('postpartum.nav.warnings', 'Warnings')}
            </Link>
          </div>
          {!postpartumInfo && !loading && (
            <div className="postpartum-message">
              <p>📋 {t('postpartum.message.selectSection', 'Click on any section above to view detailed postpartum information.')}</p>
            </div>
          )}
          {error && <div className="postpartum-form-error">{error}</div>}
        </div>
      )}
    </div>
  );
}

export default PostpartumView;
